// index.tsx
// CRITICAL: This MUST be the first import to fix Electron file:// protocol
import './set-public-path';

import { markBuildStale, isStaleChunkError, requestReload } from './utils/chunkRecovery';

// Recover from stale chunk hashes after a deployment. Vite fires
// 'vite:preloadError' when a dynamic import or CSS preload fails to load
// (e.g. the user has old HTML cached pointing at chunk hashes that no longer
// exist). One hard reload fetches fresh HTML with current hashes.
//
// Loop protection and the reload itself are centralised in chunkRecovery.ts —
// this is the ONE 'vite:preloadError' listener in the app. Only prevent the
// default (rethrow) behavior when a reload is actually happening; once the
// reload budget is exhausted, let the error propagate so it surfaces through
// the normal ErrorBoundary path (which renders the "new version" fallback).
window.addEventListener('vite:preloadError', (event) => {
  if (requestReload('vitePreloadError')) {
    event.preventDefault();
  }
});

// vite:preloadError only fires for Vite's own modulepreload machinery. A raw
// `await import(...)` that isn't wrapped in lazyWithReload (there are many
// scattered through the app, e.g. `await import('./config/authRoutes')`
// below) rejects directly with the same "Failed to fetch dynamically
// imported module" message but WITHOUT going through that event — it would
// otherwise become an unhandled rejection with no recovery path. Catch it
// here using the same shared policy.
window.addEventListener('unhandledrejection', (event) => {
  if (isStaleChunkError(event.reason) && requestReload('unhandledRejection')) {
    event.preventDefault();
  }
});

// Stale-build polling: every 60 s, compare the deployed backend build ID against
// the build ID baked into this bundle.  When they diverge, set the stale-build
// flag so the next error boundary render (on any error) triggers a reload.
const _buildId: string = (import.meta.env.VITE_BUILD_ID as string) || '';

async function _checkBuildFreshness(): Promise<void> {
  if (!_buildId) return;
  try {
    const res = await fetch('/api/health', { cache: 'no-store' });
    if (!res.ok) return;
    const data: { build_id?: string } = await res.json();
    if (data.build_id && data.build_id !== _buildId) {
      markBuildStale();
    }
  } catch {
    // Network errors are expected; don't log
  }
}

// Start polling after 30 s (let the app boot first) then every 60 s.
if (typeof window !== 'undefined' && _buildId) {
  setTimeout(() => {
    _checkBuildFreshness();
    setInterval(_checkBuildFreshness, 60_000);
  }, 30_000);
}

// Safari does not support requestIdleCallback / cancelIdleCallback.
// Polyfill with setTimeout so all call sites work without guards.
if (typeof window !== 'undefined' && !('requestIdleCallback' in window)) {
  (window as any).requestIdleCallback = (cb: IdleRequestCallback, opts?: IdleRequestOptions) => {
    const start = Date.now();
    return window.setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      } as IdleDeadline);
    }, 1);
  };
  (window as any).cancelIdleCallback = (id: number) => window.clearTimeout(id);
}

import { captureUtmParams, captureRefCode } from './services/utm/utmService';
import { buildAuthCompletePayload, PENDING_ORIGIN_KEY } from './services/utm/buildAuthCompletePayload';
captureUtmParams();
captureRefCode();

import React from 'react';
import { initSentryDeferred, getSentry } from './services/monitoring/lazySentry';
import { initLongTaskMonitor } from './utils/longTaskMonitor';

// Sentry (SDK + tracing/replay integrations, ~90KB gzip) is loaded off the
// render-critical path — see lazySentry.ts for the deferred init + the
// native error buffer that covers the gap before it's ready.
initSentryDeferred();

// PerformanceObserver-based freeze/long-task sampling — cheap in both dev
// and prod (event-driven, no polling loop). See longTaskMonitor.ts.
initLongTaskMonitor();

// Phosphor icons are lazy-loaded per icon name (see usePhosphorIcon.ts) — each
// distinct icon name a page actually renders fetches its own small chunk on
// first use. There's no whole-library preload to kick off here: that used to
// mean every page (including /signup and /login, which render zero Phosphor
// icons) paid to fetch the ~1500-icon library upfront regardless of use.

import './index.css';

// Schema pre-loading moved to lazy load - only when DocsApp is actually used
// This prevents loading heavy editor dependencies on initial page load
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { MobileModeProvider } from './contexts/MobileModeContext';
import { useAuth } from './hooks/useAuth';

import App from './App';
import { RootErrorBoundary } from './components/ErrorBoundary/RootErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { createFileLogger } from './utils/logger';
import { ConversionHandler } from './pages/marketing/convert/shared/ConversionHandler';
import { userTrackingService } from './services/users/userTrackingService';
import { triggerGoogleAdsConversion } from './utils/analyticsHelpers';
import { API_BASE_URL } from './api/config';
import useAnalytics from './hooks/useAnalytics';
import { initializeEnvironmentFavicon } from './hooks/useDocumentTitle';
import electronTrackingService from './services/users/electronTrackingService';
import { IS_DEV } from './utils/devMode';
import { tokenRefresher } from './services/tokenRefresher';
import { isAuthCompleteClaimedByAuthForm } from './components/Auth/authCompleteCoordinator';
import { installLegacyImageUrlRepair } from './services/stableFileUrl';


// Initialize environment-specific favicon
initializeEnvironmentFavicon();

// Repair images saved with a presigned URL before uploads returned stable ones —
// those expired after 7 days and are now broken wherever they were embedded.
installLegacyImageUrlRepair();

const log = createFileLogger('INDEX');

// Push the auth token to the Electron main process cache whenever it changes.
// This lets the main process read the token synchronously without calling
// executeJavaScript back into the renderer (which interrupts the renderer thread).
//
// We only push when the token is non-empty. Pushing null would clobber a valid
// main-process cache during a renderer reload race (Auth0 silent refresh hadn't
// finished hydrating localStorage when this module loaded), which silently
// disables the Electron heartbeat and RPA poll loop. Real sign-out clears the
// cache explicitly via `riverOS.auth.clearToken()` from logoutUtils.
if (typeof window !== 'undefined' && (window as any).riverOS?.auth?.pushToken) {
  const pushTokenToMain = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      (window as any).riverOS.auth.pushToken(token);
    }
  };
  window.addEventListener('auth-refresh', pushTokenToMain);
  pushTokenToMain();
}

// Note: toastService is now handled via React components in App.tsx

/**
 * TokenHandler Component
 * 
 * ARCHITECTURE OVERVIEW:
 * =====================
 * This app supports TWO authentication methods:
 * 
 * 1. **Auth0 OAuth** (Primary - for new users)
 *    - Industry-standard OAuth provider
 *    - Used for: Google sign-in, email/password via Auth0 Universal Login
 *    - Tokens stored by Auth0 SDK in: @@auth0spajs@@::... (localStorage)
 *    - Token also extracted and stored as 'authToken' for API calls
 * 
 * 2. **Custom JWT** (Legacy - for existing users)
 *    - Direct email/password login via LoginGUI.tsx
 *    - Backend issues JWT tokens
 *    - Tokens stored as: 'authToken', 'id_token'
 *    - Still supported for backwards compatibility
 * 
 * TOKEN STORAGE:
 * ==============
 * - Auth0 SDK: @@auth0spajs@@::CLIENT_ID::AUDIENCE::SCOPE (localStorage, all environments)
 * - Our API token: 'authToken' (used in 174+ places for API calls)
 * - ID token: 'id_token' (contains user email, used by useAuth)
 * 
 * CACHE LOCATION:
 * ===============
 * cacheLocation='localstorage' everywhere (localhost and production).
 * 
 * With useRefreshTokens=true and offline_access scope, Auth0 uses Refresh Token
 * Rotation — each use of a refresh token issues a new one and invalidates the old.
 * This makes localstorage safe enough: stolen tokens are single-use and rotate
 * immediately. The prior 'memory' strategy caused sign-outs every few hours because
 * the refresh token was lost on any page reload or browser tab suspension, leaving
 * getAccessTokenSilently() unable to renew an expired access token.
 * 
 * RESPONSIBILITIES:
 * =================
 * 1. Extract Auth0 access token and store as 'authToken' for API calls
 * 2. Complete backend authentication (/api/auth/complete)
 * 3. Handle funnel/persona context for onboarding flows
 * 4. Track user authentication in analytics
 * 5. Skip auth completion for routes that don't need it
 * 
 * See also:
 * - config/authRoutes.ts - Routes that skip auth completion
 * - hooks/useAuth.ts - Unified auth hook (Auth0 + custom JWT)
 * - pages/BlogAuthCallback.tsx - Subdomain auth callback handler
 */
const TokenHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, getIdTokenClaims, loginWithRedirect, isAuthenticated, auth0IsAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [hasCompletedAuth, setHasCompletedAuth] = React.useState(false);
  const { trackLogin } = useAnalytics();

  // Track Electron app launch on component mount
  React.useEffect(() => {
    electronTrackingService.trackAppStartup();
  }, []);

  // Register the Auth0 silent-refresh function so non-hook code (fetchService, etc.) can use it.
  // useLayoutEffect fires synchronously after DOM mutations, before the browser paints, so the
  // refresher is available before any child component mounts and calls tryRefresh().
  React.useLayoutEffect(() => {
    if (getAccessTokenSilently) {
      tokenRefresher.register(getAccessTokenSilently, import.meta.env.VITE_AUTH0_AUDIENCE || '');
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    const storeTokenAndCompleteAuth = async () => {
      // Short-circuit BEFORE logging so we don't spam the console on every
      // re-render caused by churn in `getAccessTokenSilently`/`navigate`/etc.
      if (hasCompletedAuth) return;
      if (!isAuthenticated || !user) return;

      if (IS_DEV) {
        log.log('[TokenHandler] Effect triggered:', {
          isAuthenticated,
          hasUser: !!user,
          hasCompletedAuth,
          userSub: user?.sub,
        });
      }

      // Identify the user for analytics/Sentry as soon as we know who they are —
      // deliberately BEFORE the branches below that can return early (skip-auth
      // routes, custom-JWT dedup), so replays/errors are always attributed to the
      // signed-in user regardless of which auth-completion path this session takes.
      userTrackingService.setUser(user.sub || '');
      getSentry().then((Sentry) => {
        Sentry?.setUser({
          id: user.sub || undefined,
          email: user.email || undefined,
          username: user.name || user.nickname || undefined,
        });
      });

      // Custom-JWT users (email/password via useAuthForm) are handled entirely by
      // useAuthForm.handleLoginRedirect which already calls /auth/complete. Skip here
      // to prevent a duplicate concurrent call that can create duplicate orgs.
      if (!auth0IsAuthenticated) {
        if (IS_DEV) log.log('[TokenHandler] Custom-JWT session — /auth/complete handled by useAuthForm, skipping');
        setHasCompletedAuth(true);
        return;
      }

      // A page-context Google-OAuth flow (useAuthForm's OAuth-completion effect,
      // triggered by this same user explicitly clicking a Google button — not a
      // modal) claims this via authCompleteCoordinator before we get here, since
      // it needs the funnel-aware post-signup dispatcher this generic path doesn't
      // have. Defer entirely rather than firing a second, concurrent /auth/complete
      // for the same brand-new signup — see authCompleteCoordinator.ts.
      if (isAuthCompleteClaimedByAuthForm()) {
        if (IS_DEV) log.log('[TokenHandler] Google auth already being handled by useAuthForm (page context) — skipping');
        setHasCompletedAuth(true);
        return;
      }

      // Check if there's already a custom auth token in localStorage
      const existingToken = localStorage.getItem('authToken');
      if (existingToken) {
        if (IS_DEV) log.log('[TokenHandler] Auth token already exists, checking if auth completion needed');
        
        // Check if we're on a route that should skip auth flow entirely
        // Uses centralized configuration from authRoutes.ts
        try {
          const { shouldSkipAuthCompletion } = await import('./config/authRoutes');
          const currentPath = window.location.pathname;
          
          if (shouldSkipAuthCompletion(currentPath)) {
            if (IS_DEV) {
              log.log('[TokenHandler] On skip-auth route with existing token - skipping auth completion entirely');
              log.log('[TokenHandler] Current path:', currentPath);
            }
            setHasCompletedAuth(true);
            return;
          }
        } catch (error) {
          log.error('[TokenHandler] Failed to load authRoutes config, continuing with auth completion:', error);
          // Fallback: Don't skip auth (safer to complete auth than skip)
          // Continue with auth completion flow below
        }
        
        // But still complete auth if we haven't yet (for non-app routes)
        if (!hasCompletedAuth) {
          if (IS_DEV) log.log('[TokenHandler] Will proceed with auth completion using existing token');
          // Continue to auth completion below
        } else {
          return;
        }
      }

      let token = existingToken;
      
      try {
        if (!existingToken) {
          if (IS_DEV) {
            log.log('[TokenHandler] Attempting to get Auth0 access token silently...');
            log.log('[TokenHandler] Auth0 config:', {
              domain: import.meta.env.VITE_AUTH0_DOMAIN,
              clientId: import.meta.env.VITE_AUTH0_CLIENT_ID?.substring(0, 10) + '...',
              audience: import.meta.env.VITE_AUTH0_AUDIENCE
            });
          }
          
          token = (await getAccessTokenSilently?.({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
              scope: 'openid profile email offline_access'
            }
          })) || null;
          
          if (IS_DEV) {
            log.log('[TokenHandler] Successfully got Auth0 access token');
            log.log('[TokenHandler] Token length:', token?.length);
          }
          if (token) {
            localStorage.setItem('authToken', token);
            window.dispatchEvent(new Event('auth-refresh'));
          }
        }
        
        
        // ✅ Only set if non-empty (filter out empty strings)
        const hasValidEmail = user.email && user.email.trim() !== '';
        const hasValidName = user.name || user.nickname;
        
        if (hasValidEmail || hasValidName) {
          userTrackingService.updateUserInfo({
            email: hasValidEmail ? user.email : undefined,
            name: hasValidName ? (user.name || user.nickname) : undefined,
            userId: user.sub || undefined
          });
        }
        
        // Identify flows through the analytics facade (analytics.identify() handles PostHog).
        // Direct posthog.identify bypasses are intentionally removed to avoid duplicate/early
        // calls that break anonymous→user alias stitching.

        // Complete authentication with backend (create user/space if needed)
        if (!hasCompletedAuth && token) {
          if (IS_DEV) {
            log.log('[TokenHandler] Completing Auth0 authentication with backend...');
            log.log('[TokenHandler] API endpoint:', `${API_BASE_URL}/auth/complete`);
            log.log('[TokenHandler] Token preview:', token.substring(0, 20) + '...');
          }
          
          // Decide whether the backend should skip space creation. Funnel
          // id / persona / page slug are read inside buildAuthCompletePayload
          // below; here we only need pendingFunnelId + pendingTemplateId to
          // gate space creation, since landing-page / tool funnels create
          // their own workspace post-OAuth.
          const pendingFunnelId = localStorage.getItem('pendingFunnelId');
          const pendingTemplateId = localStorage.getItem('pendingTemplateId');

          const { isToolFunnel } = await import('./config/toolFunnels');

          const landingContext = sessionStorage.getItem('landing_hero_context');
          const isToolFunnelValue = isToolFunnel(pendingFunnelId);
          const skipSpaceCreation =
            (!!landingContext && pendingFunnelId === 'river_docs_landing_v1') ||
            isToolFunnelValue ||
            !!pendingTemplateId;

          if (IS_DEV && skipSpaceCreation) {
            if (isToolFunnelValue) {
              console.log('🛠️  [TokenHandler] Tool funnel detected - requesting backend to skip space creation');
              console.log('🛠️  [TokenHandler] Funnel ID:', pendingFunnelId);
            } else {
              console.log('🏗️ [TokenHandler] Landing page context detected - requesting backend to skip space creation');
            }
          }

          
          try {
            // The backend needs a live ID token (not the access token above) to
            // link this identity onto an existing account via Auth0's Management
            // API when it detects a same-email conflict under a different
            // provider — see services/account_linking_service.py. Best-effort:
            // if this fails, the backend just blocks the rarer link case with a
            // clear message instead of crashing the whole auth-completion flow.
            let idTokenForLinking: string | undefined;
            try {
              const idTokenClaims = await getIdTokenClaims?.();
              idTokenForLinking = idTokenClaims?.__raw;
            } catch (idTokenErr) {
              if (IS_DEV) log.warn('[TokenHandler] Could not fetch ID token claims (non-fatal):', idTokenErr);
            }

            const body = buildAuthCompletePayload({ skip_space_creation: skipSpaceCreation, id_token: idTokenForLinking });
            if (IS_DEV) {
              log.log('[TokenHandler] Sending /auth/complete body:', body);
            }

            const response = await fetch(`${API_BASE_URL}/auth/complete`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(body)
            });

            if (IS_DEV) {
              log.log('[TokenHandler] Response status:', response.status);
              log.log('[TokenHandler] Response ok:', response.ok);
            }

            if (response.ok) {
              const result = await response.json();
              
              if (IS_DEV) log.log('[TokenHandler] Authentication completed:', result);

              if (result.requires_reauth) {
                // The backend just linked this identity onto a pre-existing
                // account instead of provisioning a new one — no User/space
                // was created for the identity we're currently holding a token
                // for. The browser's session is still pinned to that now-
                // secondary identity, so a silent refresh would just re-mint
                // the same token; force an interactive re-login for the same
                // connection, which now resolves to the primary/canonical sub.
                log.log('[TokenHandler] Account linked — forcing interactive re-login for canonical account');
                setHasCompletedAuth(true);
                localStorage.removeItem('authToken');
                localStorage.removeItem('id_token');
                await loginWithRedirect?.();
                return;
              }

              const { useOnboardingStore } = await import('./stores/onboardingStore');
              const onboardingStore = useOnboardingStore.getState();
              onboardingStore.setIsNewUser(result.is_new_user || false);
              onboardingStore.setShouldOpenChat(result.is_new_user || false);

              // Give brand-new users a wider chat panel so onboarding feels
              // prominent. No-op if a size is already stored on this browser.
              if (result.is_new_user) {
                const { seedFirstSignupChatPanelSize } = await import('./utils/chatPanelSize');
                seedFirstSignupChatPanelSize();
              }

              // Clean up pre-signup onboarding data from localStorage after successful signup
              try {
                localStorage.removeItem('pre_signup_onboarding_answers');
                log.log('[TokenHandler] Cleaned up pre-signup onboarding data');
              } catch (err) {
                log.error('[TokenHandler] Failed to clean up pre-signup data:', err);
              }
              
              // ✅ FIX: Sync dataspace context from backend response
              // This ensures frontend context matches backend immediately after auth
              if (result.dataspace_context) {
                try {
                  const { useDataspaceContextStore } = await import('./stores/dataspaceContextStore');
                  const { default: useSystemStore } = await import('./stores/systemStore');
                  
                  const normalizedContext = {
                    type: result.dataspace_context.type,
                    orgId: result.dataspace_context.org_id || null,
                    orgName: result.dataspace_context.org_name || null
                  };
                  
                  if (IS_DEV) {
                    log.log('[TokenHandler] 🔄 Syncing dataspace context from backend:', normalizedContext);
                  }
                  
                  // Update both stores
                  useDataspaceContextStore.setState({ currentContext: normalizedContext });
                  useSystemStore.setState({ currentContext: normalizedContext });
                  
                  if (IS_DEV) {
                    log.log('[TokenHandler] ✅ Dataspace context synced');
                  }
                } catch (contextError) {
                  log.error('[TokenHandler] Failed to sync dataspace context:', contextError);
                }
              }
              
              setHasCompletedAuth(true);

              // Apply dark mode for new users from creative/NSFW routes. Guide pages
              // write pendingTheme to sessionStorage; useAuthForm.handleGoogleAuth writes
              // to localStorage (survives the OAuth popup redirect). Check both.
              if (result.is_new_user) {
                const pendingTheme =
                  sessionStorage.getItem('pendingTheme') ?? localStorage.getItem('pendingTheme');
                if (pendingTheme === 'dark') {
                  localStorage.setItem('theme', 'dark');
                  document.documentElement.setAttribute('data-theme', 'dark');
                  document.documentElement.classList.remove('theme-scheme-light');
                  document.documentElement.classList.add('theme-scheme-dark');
                }
                sessionStorage.removeItem('pendingTheme');
                localStorage.removeItem('pendingTheme');
              }

              // Track Electron app login after successful authentication
              electronTrackingService.trackAppLogin();
              
              // Track successful signup for new OAuth users (if space_id indicates new user)
              if (result.space_id && user) {
                if (IS_DEV) log.log('[TokenHandler] OAuth user detected - tracking signup conversion');
                
                // signup_completed is tracked server-side; identify here.
                trackLogin('google_oauth', user.sub || 'unknown', {
                  email: user.email,
                  name: user.name || user.nickname,
                  auth_method: 'google_oauth',
                  is_new_user: true,
                });
                
                // Trigger Google Ads conversion tracking
                triggerGoogleAdsConversion();
              }
              
              const authCurrentPath = window.location.pathname;

              const { shouldSkipPostAuthRedirect } = await import('./config/authRoutes');
              if (shouldSkipPostAuthRedirect(authCurrentPath)) {
                if (IS_DEV) {
                  log.log('[TokenHandler] On destination route — auth completed, skipping redirect:', authCurrentPath);
                }
                return;
              }
              
              // Check for pending funnel/redirect info from signup modal
              const pendingFunnelId = localStorage.getItem('pendingFunnelId');
              const pendingRedirectTo = sessionStorage.getItem('pendingRedirectTo') || localStorage.getItem('pendingRedirectTo');
              const pendingPersona = localStorage.getItem('pendingPersona');
              
              if (IS_DEV) {
                log.log('[TokenHandler] Checking for pending funnel info:', {
                  pendingFunnelId,
                  pendingRedirectTo,
                  pendingPersona
                });
              }
              
              // Check if there's a custom redirect specified (takes priority)
              if (pendingRedirectTo) {
                if (IS_DEV) log.log('[TokenHandler] ✅ Using custom redirect:', pendingRedirectTo);
                localStorage.removeItem('pendingFunnelId');
                localStorage.removeItem('pendingPersona');
                localStorage.removeItem('pendingPageCategory');
                sessionStorage.removeItem('pendingPageCategory');
                localStorage.removeItem('pendingRedirectTo');
                sessionStorage.removeItem('pendingRedirectTo');
                localStorage.removeItem(PENDING_ORIGIN_KEY);
                if (pendingRedirectTo.startsWith('http://') || pendingRedirectTo.startsWith('https://')) {
                  window.location.href = pendingRedirectTo;
                } else {
                  navigate(pendingRedirectTo, { replace: true });
                }
                return;
              }
              
              // ✅ CLEAN: For tool funnels, DON'T redirect - stay on current page
              // The tool's useEffect will detect auth and process pending data
              const { isToolFunnel: checkIsToolFunnel } = await import('./config/toolFunnels');
              if (checkIsToolFunnel(pendingFunnelId)) {
                if (IS_DEV) {
                  console.log('🛠️  [TokenHandler] Tool funnel - staying on current page');
                  console.log('🛠️  [TokenHandler] Tool component will process pending data');
                  console.log('🛠️  [TokenHandler] Funnel ID:', pendingFunnelId);
                }
                // Don't clear pending context or navigate - tool needs to know user just authenticated
                return;
              }
              
              // Check if this funnel uses multi-step onboarding (only non-tool funnels)
              const { TEMPLATE_ONBOARDING_FUNNELS: templateFunnelMap } = await import('./config/toolFunnels');
              const onboardingPersona = pendingFunnelId && templateFunnelMap[pendingFunnelId];
              
              // Handle special funnel redirects - only for multi-step onboarding funnels
              if (onboardingPersona) {
                if (IS_DEV) log.log('[TokenHandler] ✅ Redirecting to multi-step onboarding:', onboardingPersona);
                localStorage.removeItem('pendingFunnelId');
                localStorage.removeItem('pendingPersona');
                localStorage.removeItem('pendingPageCategory');
                sessionStorage.removeItem('pendingPageCategory');
                localStorage.removeItem('pendingRedirectTo');
                sessionStorage.removeItem('pendingRedirectTo');
                navigate(`/onboarding/${onboardingPersona}`, { replace: true });
                return;
              }
              
              // Suppress book project promo modal for users who signed up from the book landing page
              if (pendingFunnelId === 'book_formatter_v1') {
                const useUserStore = (await import('./stores/userStore')).default;
                useUserStore.getState().setOnboardingFlag('bookProjectPromoModalShown', true);
              }

              // If user came from a space pack claim flow, navigate back to the claim page.
              // ClaimPack will detect auth and proceed with installation automatically.
              const pendingClaimPackId = sessionStorage.getItem('pendingClaimPackId');
              if (pendingClaimPackId) {
                if (IS_DEV) console.log('📦 [TokenHandler] Resuming space pack claim for:', pendingClaimPackId);
                // Keep the key in sessionStorage — ClaimPack will clear it after install
                navigate(`/claim/${pendingClaimPackId}`, { replace: true });
                return;
              }

              // If user came from a template CTA, create the template space and navigate there.
              // Backend already skipped generic space creation (skip_space_creation=true above).
              const tokenForTemplate = token;
              const templateIdToCreate = localStorage.getItem('pendingTemplateId') || sessionStorage.getItem('pendingTemplateId');
              if (templateIdToCreate && tokenForTemplate) {
                // Remove pending keys before the async call so a retry doesn't double-create.
                localStorage.removeItem('pendingTemplateId');
                sessionStorage.removeItem('pendingTemplateId');
                localStorage.removeItem('pendingFunnelId');
                localStorage.removeItem('pendingPersona');
                localStorage.removeItem('pendingPageCategory');
                sessionStorage.removeItem('pendingPageCategory');
                sessionStorage.removeItem('pendingRedirectTo');
                localStorage.removeItem('pendingPageGroup');
                localStorage.removeItem('pendingPageSlug');
                localStorage.removeItem(PENDING_ORIGIN_KEY);
                if (IS_DEV) console.log('🎯 [TokenHandler] Creating space from template:', templateIdToCreate);
                try {
                  const tRes = await fetch(`${API_BASE_URL}/spaces/from-template`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenForTemplate}` },
                    body: JSON.stringify({ templateId: templateIdToCreate })
                  });
                  if (tRes.ok) {
                    const tData = await tRes.json();
                    const templateSpaceId = tData.spaceId || tData._id || tData.id;
                    if (templateSpaceId) {
                      navigate(`/desktops/${templateSpaceId}`, { replace: true });
                      return;
                    }
                  } else {
                    log.error('[TokenHandler] from-template failed:', tRes.status);
                  }
                } catch (tErr) {
                  log.error('[TokenHandler] from-template error:', tErr);
                }
                // Template creation failed — restore the key so the user can retry
                // and surface an error rather than silently falling through.
                sessionStorage.setItem('pendingTemplateId', templateIdToCreate);
                import('./services/sessionManager').then(({ sessionManager }) => {
                  sessionManager.handleSessionExpired({
                    message: 'Could not create your workspace from that template. Please try again.'
                  });
                });
                return;
              }

              // Clear pending values
              localStorage.removeItem('pendingFunnelId');
              localStorage.removeItem('pendingPersona');
              localStorage.removeItem('pendingPageCategory');
              sessionStorage.removeItem('pendingPageCategory');
              localStorage.removeItem('pendingRedirectTo');
              sessionStorage.removeItem('pendingRedirectTo');
              localStorage.removeItem('pendingPageGroup');
              localStorage.removeItem('pendingPageSlug');
              localStorage.removeItem(PENDING_ORIGIN_KEY);
              
              // Redirect to user's space with welcome document
              const currentPath = window.location.pathname;
              const redirectUrl = result.redirect_to;
              
              if (IS_DEV) {
                log.log('[TokenHandler] Current path:', currentPath);
                log.log('[TokenHandler] Redirect URL from backend:', redirectUrl);
              }
              
              // Redeem a pending invite link for new OAuth users. This must run before
              // the !result.space_id guard below because space-pack invites set
              // skip_space_creation=true, so result.space_id is null on auth/complete.
              // Email/password signups are handled by useFunnelRedirect PATH 0e instead.
              if (result.is_new_user) {
                const pendingInviteToken = localStorage.getItem('pendingInviteToken');
                if (pendingInviteToken) {
                  localStorage.removeItem('pendingInviteToken');
                  localStorage.removeItem('pendingInviteHasSpacePack');
                  try {
                    const redeemRes = await fetch(
                      `${API_BASE_URL}/invites/token/${encodeURIComponent(pendingInviteToken)}/redeem`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                      },
                    );
                    if (redeemRes.ok) {
                      const redeemData = await redeemRes.json();
                      const parameters: Record<string, unknown> = redeemData.parameters ?? {};
                      const prompt = parameters.onboarding_prompt as string | undefined;
                      const skipAnimatedIntro = !!parameters.skip_animated_intro;
                      const skipOnboardingWizard = !!parameters.skip_onboarding_wizard;

                      // Only set wizard-skip when the intro will actually play.
                      // handleIntroComplete (in Space.tsx) clears this flag; if the
                      // intro is skipped via skipAnimatedIntro, handleIntroComplete
                      // never fires and the flag would leak into a future session.
                      if (skipOnboardingWizard && !skipAnimatedIntro) {
                        localStorage.setItem('pendingSkipOnboardingWizard', '1');
                      }
                      if (prompt) {
                        onboardingStore.setPendingSpaceKickoff(prompt, { label: 'Welcome to River!', icon: '👋' });
                      }

                      // The pack is installed server-side during redemption (see
                      // backend/routes/invites.py); the created space comes back
                      // in the redeem response — no second install round trip.
                      const installedSpace = redeemData.space_pack as { spaceId?: string } | null | undefined;
                      const targetSpaceId: string | undefined = installedSpace?.spaceId ?? result.space_id ?? undefined;

                      if (targetSpaceId) {
                        if (!skipAnimatedIntro && !onboardingStore.pendingSpaceKickoff) {
                          onboardingStore.setCameFromAnimatedIntro(true);
                        }
                        navigate(`/desktops/${targetSpaceId}`, { replace: true });
                        return;
                      }
                    } else {
                      log.warn('[TokenHandler] Invite redemption failed:', redeemRes.status, '— continuing to default flow');
                    }
                  } catch (redeemErr) {
                    log.warn('[TokenHandler] Invite redemption error (non-fatal, continuing):', redeemErr);
                  }
                }
              }

              // Check if we're on a special flow that shouldn't redirect to space
              const isOnRiverConfig = currentPath === '/river-config';
              if (isOnRiverConfig || !result.space_id) {
                if (IS_DEV) {
                  console.log('🏗️ [TokenHandler] ✅ Special signup flow detected - skipping space redirect');
                  console.log('🏗️ [TokenHandler] User will stay on:', currentPath);
                }
                // Don't navigate - stay on current flow
                return;
              }
              
              // Check if there's stored context from landing page (shouldn't happen if skip_space_creation worked)
              const storedContext = sessionStorage.getItem('landing_hero_context');
              if (storedContext && result.space_id) {
                if (IS_DEV) {
                  console.log('⚠️ [TokenHandler] Backend created space even though landing context exists!');
                  console.log('⚠️ [TokenHandler] This means skip_space_creation flag was not respected');
                  console.log('⚠️ [TokenHandler] Letting landing page create workspace anyway (Space', result.space_id, 'will be orphaned)');
                }
                // Don't navigate - let landing page handle it
                return;
              }
              
              if (redirectUrl) {
                // For new users, keep chat open (skip QuickLauncher redirect) and
                // trigger the onboarding wizard inside the space.
                if (result.is_new_user) {
                  // Remove openQuickLauncher param from URL (handles all edge cases)
                  const finalRedirectUrl = redirectUrl
                    .replace(/[?]openQuickLauncher=true&/, '?')   // ?param& -> ?
                    .replace(/&openQuickLauncher=true/g, '')       // &param -> (remove)
                    .replace(/[?]openQuickLauncher=true$/, '');    // ?param (alone) -> (remove)

                  // Trigger the onboarding wizard that email-signup users already get.
                  // Skip when a kickoff prompt is already queued (e.g. from an invite)
                  // — the kickoff fires immediately via SpacePageWrapper Path B.
                  // Accept both /spaces/ (legacy) and /desktops/ (desktop branch canonical).
                  if (
                    (finalRedirectUrl.startsWith('/spaces/') || finalRedirectUrl.startsWith('/desktops/')) &&
                    !onboardingStore.pendingSpaceKickoff
                  ) {
                    onboardingStore.setCameFromAnimatedIntro(true);
                  }

                  if (IS_DEV) {
                    log.log('[TokenHandler] ✅ New user detected - redirecting with chat open:', finalRedirectUrl);
                    log.log('[TokenHandler] Skipped QuickLauncher to show welcome message');
                  }
                  navigate(finalRedirectUrl, { replace: true });
                } else {
                  if (IS_DEV) log.log('[TokenHandler] ✅ Navigating to user workspace:', redirectUrl);
                  navigate(redirectUrl, { replace: true });
                }
              } else {
                if (IS_DEV) log.error('[TokenHandler] ❌ No redirect_to provided by backend!');
                // Fallback: navigate to space if we have space_id
                if (result.space_id) {
                  let fallbackUrl = `/desktops/${result.space_id}`;
                  
                  // Check for stored context in fallback too
                  if (storedContext) {
                    try {
                      const parsed = JSON.parse(storedContext);
                      if (parsed.inputText) {
                        fallbackUrl += `?contextKey=landing_hero_context&prompt=${encodeURIComponent(parsed.inputText)}`;
                      if (IS_DEV) console.log('🎯 [TokenHandler] Added stored context to fallback URL');
                    }
                  } catch {}
                }
                  
                  if (IS_DEV) log.log('[TokenHandler] Using fallback redirect:', fallbackUrl);
                  navigate(fallbackUrl, { replace: true });
                }
              }
            } else {
              log.error('[TokenHandler] Auth completion failed:', response.status);

              const { handleAccountLockedResponse, handleDuplicateAccountResponse } = await import('./utils/accountLockedHandler');
              const wasLocked = await handleAccountLockedResponse(response);
              if (wasLocked) {
                // Handler already cleared state and redirected — bail out.
                setHasCompletedAuth(true);
                return;
              }
              const wasDuplicate = await handleDuplicateAccountResponse(response);
              if (wasDuplicate) {
                // Handler already cleared state and redirected — bail out.
                setHasCompletedAuth(true);
                return;
              }

              if (IS_DEV) {
                try {
                  const errorText = await response.text();
                  log.error('[TokenHandler] Error response body:', errorText);
                } catch {
                  log.error('[TokenHandler] Could not read error response');
                }
              }
              // Mark as attempted so we don't retry endlessly, then surface the error.
              setHasCompletedAuth(true);
              import('./services/sessionManager').then(({ sessionManager }) => {
                sessionManager.handleSessionExpired({
                  message: 'Sign-in could not be completed. Please refresh the page or try again.'
                });
              });
            }
          } catch (authError) {
            if (IS_DEV) {
              log.error('[TokenHandler] Error completing authentication:', authError);
              if (authError instanceof Error) {
                log.error('[TokenHandler] Error message:', authError.message);
                log.error('[TokenHandler] Error stack:', authError.stack);
              }
            }
          }
        }
      } catch (error) {
        if (IS_DEV) {
          log.error('[TokenHandler] Error getting Auth0 token:', error);
          if (error instanceof Error) {
            log.error('[TokenHandler] Token error message:', error.message);
            log.error('[TokenHandler] Token error stack:', error.stack);
          }
        }
        
        // Enhanced error handling - detect expired/corrupted auth state
        const errorMessage = error instanceof Error ? error.message : '';
        const isMissingRefreshToken = errorMessage.includes('Missing Refresh Token');
        const isLoginRequired = errorMessage.includes('Login required');
        
        if (isMissingRefreshToken || isLoginRequired) {
          console.warn('🚨 [TokenHandler] Token error detected:', errorMessage);
          
          // Trigger session expired modal instead of hard logout
          import('./services/sessionManager').then(({ sessionManager }) => {
            sessionManager.handleSessionExpired({
              message: isMissingRefreshToken 
                ? 'Your session has expired. Please log in again to continue.'
                : 'Authentication error. Please log in again.'
            });
          });
          
          // Clear tokens but don't redirect immediately - let modal handle it
          import('./utils/logoutUtils').then(({ clearAllAuthState }) => {
            clearAllAuthState();
          });
        } else {
          // Other errors - just clear token
          if (!existingToken) {
            localStorage.removeItem('authToken');
          }
        }
      }
    };

    storeTokenAndCompleteAuth();
  }, [isAuthenticated, auth0IsAuthenticated, user, getAccessTokenSilently, hasCompletedAuth, navigate, logout]);

  return <>{children}</>;
};

// Reuse a single React root across Vite HMR re-executions of this entry module.
// Without this guard, an HMR update that propagates up to index.tsx re-runs
// createRoot() on the same #root node, mounting a SECOND independent <App/> as a
// sibling — which stacks two live, separately-stateful copies of the whole app.
// No-op in production (the entry runs exactly once there).
const _rootContainer = document.getElementById('root') as HTMLElement;
const _rootHost = window as unknown as { __riverReactRoot?: ReactDOM.Root };
const root = _rootHost.__riverReactRoot ?? (_rootHost.__riverReactRoot = ReactDOM.createRoot(_rootContainer));

// Support deep-linking in Electron file:// by reading target route from the search params
const initialSearch = typeof window !== 'undefined' ? window.location.search : '';
const urlParams = new URLSearchParams(initialSearch);
const forcedRoute = urlParams.get('route');

if (forcedRoute && typeof window !== 'undefined' && typeof window.history !== 'undefined') {
  try {
    // Use relative path so asset base stays within build folder
    const cleanPath = forcedRoute.startsWith('/') ? forcedRoute.substring(1) : forcedRoute;
    window.history.replaceState(null, '', cleanPath);
  } catch {}
}

// Helper function to get canonical redirect URI
const getCanonicalRedirectUri = () => {
  const host = window.location.hostname;
  if (host.includes('bedrock.computer') || host.includes('riversystem.ai') || host.includes('rivereditor.com')) {
    // Always redirect to the root domain for consistency with Auth0 callback URLs
    return `https://${host}`;
  }
  // For local development, use the current origin
  return window.location.origin;
};

root.render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <MobileModeProvider>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN || ''}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID || ''}
        authorizationParams={{
          redirect_uri: getCanonicalRedirectUri(),
          audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
          scope: 'openid profile email offline_access',
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
        cookieDomain={
          window.location.hostname.includes('bedrock.computer')
            ? '.bedrock.computer'
            : window.location.hostname.includes('riversystem.ai')
            ? '.riversystem.ai'
            : window.location.hostname.includes('rivereditor.com')
            ? '.rivereditor.com'
            : undefined
        }
        skipRedirectCallback={
          // CRITICAL: Skip OAuth callback processing on /blog-auth-callback route
          // This route relays the OAuth code to blog subdomains WITHOUT consuming it
          // If we don't skip, the code gets consumed here and blog subdomain auth fails
          window.location.pathname === '/blog-auth-callback'
        }
        onRedirectCallback={(appState) => {
          // For OAuth callback, don't immediately redirect to root
          // Let TokenHandler complete the authentication flow and determine the proper redirect
          const targetPath = appState?.returnTo;
          
          if (targetPath) {
            // If there's a specific returnTo path, use it
            console.log('🔄 [Auth0] onRedirectCallback - redirecting to specified path:', targetPath);
            window.history.replaceState(
              {},
              document.title,
              targetPath
            );
          } else {
            // If no returnTo, stay on current page and let TokenHandler handle the redirect
            // This prevents dropping users on root before authentication completes
            console.log('🔄 [Auth0] onRedirectCallback - no returnTo, letting TokenHandler handle redirect');
            // Don't navigate anywhere - TokenHandler will handle it after /auth/complete
          }
        }}
      >
        <TokenHandler>
          <ConversionHandler />
          <RootErrorBoundary>
            <App />
          </RootErrorBoundary>
        </TokenHandler>
      </Auth0Provider>
      </MobileModeProvider>
    </BrowserRouter>,
);

reportWebVitals();
